/**
 * Build-time and runtime configuration, in one place.
 *
 * `window.__IRONPACT_CONFIG__` is written into index.html by the
 * `ironpact-legacy-shell` Vite plugin so the classic legacy script can read the
 * same values before any module executes.
 */
const runtime = (typeof window !== 'undefined' && window.__IRONPACT_CONFIG__) || {};

export const APP_VERSION = runtime.version || '0.0.0-dev';
export const BUILD_TIME = runtime.buildTime || null;

/**
 * Where the marketing site / legal documents live, and the origin the native
 * shells send password-reset mail back to.
 *
 * This has to be the hostname set as the Site URL in Supabase. GoTrue validates
 * a `redirectTo` by comparing its *hostname* against the Site URL's, so a value
 * that disagrees is not rejected loudly — the redirect is dropped and the mail
 * arrives pointing at the Site URL instead. It keeps working, at a domain
 * nobody chose.
 */
export const WEB_ORIGIN = 'https://ironpact.sigro.app';

/**
 * Support inbox. Must match the address in public/privacy.html and
 * public/terms.html — the store listings point at those documents, and a
 * contact address that bounces is a review finding.
 *
 * Comes from the injected config so the legacy core's Help form and this module
 * layer cannot drift apart. The literal is the fallback for a dev server
 * started without the env var.
 */
export const SUPPORT_EMAIL = runtime.supportEmail || 'support@ironpact.sigro.app';

/**
 * RevenueCat public SDK keys, one per store. Public keys are meant to ship in a
 * client: they can begin a purchase and read entitlements, and that is all.
 * Granting anything is the webhook's job, and it holds the secret.
 *
 * Empty means billing is not configured for this build — `src/compliance` then
 * leaves the purchase buttons explaining themselves rather than half-working.
 */
export const REVENUECAT_KEYS = {
  ios: runtime.revenueCatIosKey || '',
  android: runtime.revenueCatAndroidKey || '',
};

/**
 * Deep-link scheme registered by the native shells. Used for OAuth and
 * password-reset redirects so the system browser can hand control back to the
 * app instead of stranding the user on a web page.
 */
export const APP_SCHEME = 'ironpact';

/**
 * Hostnames whose links the native shells should swallow rather than hand to a
 * browser.
 *
 * A list, not a single value, because the Vercel hostname is what every link
 * shared or mailed before the move to the custom domain points at, and those
 * are in people's messages and inboxes for good. Both hostnames serve the same
 * build, so both should open the installed app.
 */
export const DEEP_LINK_HOSTS = ['ironpact.sigro.app', 'ironpact-xi.vercel.app'];
